import { Injectable } from '@angular/core';

export const LOCAL_STORAGE_KEYS = {
  SELECTED_BU: 'centaur.selected_bu',
  SELECTED_AREA: 'centaur.selected_area',
  SELECTED_SUB_AREA: 'centaur.selected_sub_area',
} as const;

export type LocalStorageKey =
  (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];

export interface StoredSelection {
  id: number;
  name: string;
}

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  /**
   * Retrieves and JSON-parses a value from localStorage.
   * Returns null if the key doesn't exist or the stored value is malformed.
   */
  get<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  /**
   * JSON-serialises a value and stores it in localStorage.
   * Silently ignores storage errors (e.g. private-mode quota exceeded).
   */
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  }

  /**
   * Removes a single key from localStorage.
   */
  remove(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Clears all keys from localStorage.
   */
  clearAll(): void {
    localStorage.clear();
  }

  // ─── Typed convenience methods ───────────────────────────────────────────────

  getSelectedBU(): StoredSelection | null {
    return this.get<StoredSelection>(LOCAL_STORAGE_KEYS.SELECTED_BU);
  }

  setSelectedBU(value: StoredSelection | null): void {
    if (value) {
      this.set(LOCAL_STORAGE_KEYS.SELECTED_BU, value);
    } else {
      this.remove(LOCAL_STORAGE_KEYS.SELECTED_BU);
    }
  }

  getSelectedArea(): StoredSelection | null {
    return this.get<StoredSelection>(LOCAL_STORAGE_KEYS.SELECTED_AREA);
  }

  setSelectedArea(value: StoredSelection | null): void {
    if (value) {
      this.set(LOCAL_STORAGE_KEYS.SELECTED_AREA, value);
    } else {
      this.remove(LOCAL_STORAGE_KEYS.SELECTED_AREA);
    }
  }

  getSelectedSubArea(): StoredSelection | null {
    return this.get<StoredSelection>(LOCAL_STORAGE_KEYS.SELECTED_SUB_AREA);
  }

  setSelectedSubArea(value: StoredSelection | null): void {
    if (value) {
      this.set(LOCAL_STORAGE_KEYS.SELECTED_SUB_AREA, value);
    } else {
      this.remove(LOCAL_STORAGE_KEYS.SELECTED_SUB_AREA);
    }
  }
}
